import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { useUserAccess } from '@/hooks/useUserAccess';
import { Loader2, UserCircle, FileText, BarChart, Search, SortDesc, SortAsc } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { UserType } from '@/types/user';
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type UserReportData = {
  email: string;
  userType: UserType;
  reportsGenerated: number;
  reportsRemaining: number;
  maxReports: number;
};

type SortField = 'email' | 'userType' | 'reportsGenerated' | 'reportsRemaining';
type SortDirection = 'asc' | 'desc';

const AdminDashboard = () => {
  const [userData, setUserData] = useState<UserReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('reportsGenerated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { checkUserAccess } = useUserAccess();
  const [userAccess, setUserAccess] = useState<{ isAdmin: boolean } | null>(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const access = await checkUserAccess();
        setUserAccess(access);
        
        if (!access.isAdmin) {
          toast({
            title: "Access Denied",
            description: "You don't have permission to view this page.",
            variant: "destructive",
          });
          navigate('/chat');
          return;
        }
        
        fetchUserData();
      } catch (error) {
        console.error("Error checking admin access:", error);
        navigate('/chat');
      }
    };
    
    checkAccess();
  }, []);

  if (userAccess && !userAccess.isAdmin) {
    return <p className="text-center p-8 text-xl text-red-500 font-bold">Access Denied</p>;
  }

  const getMaxReports = (userType: UserType): number => {
    switch (userType) {
      case 'starter':
        return 10;
      case 'pro':
        return 30;
      case 'premium':
        return 100;
      case 'admin':
        return Infinity;
      case 'free':
      default:
        return 2;
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_type, used_free_report');
      
      if (profilesError) throw profilesError;
      
      const processedUsers: UserReportData[] = [];
      
      if (profiles && profiles.length > 0) {
        for (const profile of profiles) {
          let userEmail = "Unknown User";
          if (profile.id === (await supabase.auth.getUser()).data.user?.id) {
            userEmail = (await supabase.auth.getUser()).data.user?.email || "Unknown Email";
          }
          
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);
          const monthStart = startOfMonth.toISOString();
          
          const { count } = await supabase
            .from('chat_history')
            .select('id', { count: 'exact' })
            .eq('user_id', profile.id)
            .gte('created_at', monthStart);
          
          const userType = profile.user_type as UserType;
          const maxReports = getMaxReports(userType);
          const reportsGenerated = count || 0;
          const reportsRemaining = Math.max(0, maxReports - reportsGenerated);
          
          processedUsers.push({
            email: userEmail,
            userType,
            reportsGenerated,
            reportsRemaining,
            maxReports
          });
        }
      }
      
      const currentUser = (await supabase.auth.getUser()).data.user;
      if (currentUser && !processedUsers.some(u => u.email === currentUser.email)) {
        processedUsers.push({
          email: currentUser.email || "kintupercy@gmail.com",
          userType: 'admin',
          reportsGenerated: 0,
          reportsRemaining: Infinity,
          maxReports: Infinity
        });
      }
      
      setUserData(processedUsers);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast({
        title: "Error",
        description: "Failed to load user data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedUsers = () => {
    return userData
      .filter(user => {
        const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || user.userType === filterType;
        return matchesSearch && matchesType;
      })
      .sort((a, b) => {
        if (sortField === 'email') {
          return sortDirection === 'asc' 
            ? a.email.localeCompare(b.email)
            : b.email.localeCompare(a.email);
        } else if (sortField === 'userType') {
          return sortDirection === 'asc'
            ? a.userType.localeCompare(b.userType)
            : b.userType.localeCompare(a.userType);
        } else if (sortField === 'reportsGenerated') {
          return sortDirection === 'asc'
            ? a.reportsGenerated - b.reportsGenerated
            : b.reportsGenerated - a.reportsGenerated;
        } else if (sortField === 'reportsRemaining') {
          if (a.reportsRemaining === Infinity && b.reportsRemaining === Infinity) return 0;
          if (a.reportsRemaining === Infinity) return sortDirection === 'asc' ? 1 : -1;
          if (b.reportsRemaining === Infinity) return sortDirection === 'asc' ? -1 : 1;
          
          return sortDirection === 'asc'
            ? a.reportsRemaining - b.reportsRemaining
            : b.reportsRemaining - a.reportsRemaining;
        }
        return 0;
      });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getMostActiveUser = () => {
    if (userData.length === 0) return null;
    return userData.reduce((prev, current) => 
      (prev.reportsGenerated > current.reportsGenerated) ? prev : current
    );
  };

  const mostActiveUser = getMostActiveUser();
  const filteredUsers = getFilteredAndSortedUsers();

  const handleRefresh = () => {
    fetchUserData();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-lg">Checking access...</span>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-gray-500 mt-1">Monitor user report usage across all accounts</p>
        </div>
        <button 
          onClick={handleRefresh}
          className="bg-blue-600 hover:bg-blue-500 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all flex items-center"
        >
          <BarChart className="h-4 w-4 mr-2" />
          Refresh Data
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="p-4 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="flex items-center">
            <UserCircle className="h-8 w-8 mr-3 text-blue-300" />
            <div>
              <h3 className="text-sm font-medium text-blue-300">Total Users</h3>
              <p className="text-2xl font-bold">{userData.length}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-indigo-900 to-indigo-800 text-white">
          <div className="flex items-center">
            <FileText className="h-8 w-8 mr-3 text-indigo-300" />
            <div>
              <h3 className="text-sm font-medium text-indigo-300">Total Reports</h3>
              <p className="text-2xl font-bold">
                {userData.reduce((sum, user) => sum + user.reportsGenerated, 0)}
              </p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-purple-900 to-purple-800 text-white">
          <div className="flex items-center">
            <BarChart className="h-8 w-8 mr-3 text-purple-300" />
            <div>
              <h3 className="text-sm font-medium text-purple-300">Most Active User</h3>
              <p className="text-2xl font-bold truncate" title={mostActiveUser?.email || ''}>
                {mostActiveUser?.reportsGenerated || 0} reports
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="mb-6 space-y-4">
        <h2 className="text-xl font-semibold">User Analytics</h2>
        
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search users by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="free">Free</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg">Loading user data...</span>
        </div>
      ) : (
        <div className="bg-gray-950 rounded-lg border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-800">
              <thead className="bg-gray-900">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      {sortField === 'email' && (
                        sortDirection === 'asc' ? 
                          <SortAsc className="ml-1 h-3 w-3" /> : 
                          <SortDesc className="ml-1 h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('userType')}
                  >
                    <div className="flex items-center">
                      Plan Type
                      {sortField === 'userType' && (
                        sortDirection === 'asc' ? 
                          <SortAsc className="ml-1 h-3 w-3" /> : 
                          <SortDesc className="ml-1 h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('reportsGenerated')}
                  >
                    <div className="flex items-center">
                      Reports Generated
                      {sortField === 'reportsGenerated' && (
                        sortDirection === 'asc' ? 
                          <SortAsc className="ml-1 h-3 w-3" /> : 
                          <SortDesc className="ml-1 h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('reportsRemaining')}
                  >
                    <div className="flex items-center">
                      Reports Remaining
                      {sortField === 'reportsRemaining' && (
                        sortDirection === 'asc' ? 
                          <SortAsc className="ml-1 h-3 w-3" /> : 
                          <SortDesc className="ml-1 h-3 w-3" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Usage
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      No users match your search criteria
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-900">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-300 truncate max-w-[200px]" title={user.email}>
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.userType === 'admin' ? 'bg-green-900 text-green-300' :
                          user.userType === 'premium' ? 'bg-purple-900 text-purple-300' :
                          user.userType === 'pro' ? 'bg-blue-900 text-blue-300' :
                          user.userType === 'starter' ? 'bg-gray-700 text-gray-300' :
                          user.userType === 'free' ? 'bg-orange-900 text-orange-300' :
                          'bg-orange-900 text-orange-300'
                        }`}>
                          {user.userType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {user.reportsGenerated}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {user.reportsRemaining === Infinity ? 'Unlimited' : user.reportsRemaining}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.userType !== 'admin' && (
                          <div className="w-full bg-gray-800 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                user.reportsGenerated / user.maxReports > 0.8 ? 'bg-red-500' : 
                                user.reportsGenerated / user.maxReports > 0.5 ? 'bg-yellow-500' : 'bg-blue-500'
                              }`}
                              style={{ 
                                width: `${Math.min(100, (user.reportsGenerated / (user.maxReports || 1)) * 100)}%` 
                              }}
                            ></div>
                          </div>
                        )}
                        {user.userType === 'admin' && (
                          <span className="text-xs text-green-500">Unlimited Access</span>
                        )}
                        {user.userType === 'free' && (
                          <span className="text-xs text-orange-500">Free Tier (2 reports)</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 bg-gray-900 border-t border-gray-800">
            <p className="text-xs text-gray-500">
              Showing {filteredUsers.length} of {userData.length} users
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
